// `input` — one or more fields, drawn by the SHARED FormRenderer restricted to
// the listed columns, so widgets, masks, LOVs, people pickers, dates and rich
// text are the form's own (docs/plans/transaction-wizard.md §6.3). Header
// fields and current-line fields are two FormRenderers over the same runtime;
// a `var.X` field is a plain input of its declared type.

import { useMemo } from 'react'
import FormRenderer from '../../txn/FormRenderer'
import { TxnRuntimeProvider } from '../../txn/context'
import type { ColumnDef, SqlModel } from '../../txn/types'
import { isMandatory } from '../../txn/utils'
import { columnOf, keyOf, parseTarget, text, type FormIndex, type Target } from '../model'
import { T, input as inputStyle, muted } from '../styles'
import { tw } from '../../testHooks'
import type { WizardStep } from '../contract'
import type { StepProps } from './common'

interface FieldSpec {
  spec: string
  target: Target
  label?: string
  help?: string
  required?: boolean
  type?: string
}

function fieldsOf(step: WizardStep, fi: FormIndex, form: string): FieldSpec[] {
  const out: FieldSpec[] = []
  for (const f of step.fields || []) {
    const o = typeof f === 'string' ? { field: f } as { field: string; label?: string; help?: string; required?: boolean; type?: string } : f
    const t = parseTarget(o.field, fi, form)
    if (t) out.push({ spec: o.field, target: t, label: o.label, help: o.help, required: o.required, type: o.type })
  }
  return out
}

/** The form restricted to the step's columns, in the step's order, in one
 *  always-open group titled by the step. A field the form hides is shown —
 *  the wizard asked for it. Labels override presentation only; `required` can
 *  only make a field stricter than the model's `mandatory`. */
function restrict(sm: SqlModel, fi: FormIndex, formNo: string, fields: FieldSpec[], group: string): SqlModel {
  const columns: Array<{ column: ColumnDef }> = []
  fields.forEach((f, i) => {
    const col = columnOf(fi, formNo, f.target.name)
    if (!col) return
    const c: ColumnDef = {
      ...col,
      group_name: group,
      y: String(i),
      hidden: false,
      ...(f.label ? { heading: f.label } : {}),
      ...(f.required && !isMandatory(col) ? { mandatory: true } : {}),
    } as ColumnDef
    columns.push({ column: c })
  })
  return {
    ...sm,
    columns,
    actions: [],
    open_groups: [group],
    group_settings: { [group]: { label: group, open: true } },
    display_in_group: undefined,
  }
}

export default function InputStep({ ctl, handheld }: StepProps) {
  const { step, scope, fi, frame, base, screen, ui, host, issues } = ctl
  const form = String(step?.form_no || frame?.line_form || fi?.lineForm || '')
  const rawFields = useMemo(() => (step && fi ? fieldsOf(step, fi, form) : []), [step, fi, form])
  if (!step || !scope || !fi || !frame || !base || !screen) return null
  // Labels and help are templates, like a step's title ("Qty ({line.uom})").
  const fields = rawFields.map(f => ({
    ...f,
    label: f.label ? text(f.label, scope) : f.label,
    help: f.help ? text(f.help, scope) : f.help,
  }))

  const group = step.title ? text(step.title, scope) : 'Details'
  const headerFields = fields.filter(f => f.target.scope === 'header')
  const lineFields = fields.filter(f => f.target.scope === 'line')
  const varFields = fields.filter(f => f.target.scope === 'var')
  const locked = new Set(frame.locked || [])
  const lockedKeys = (formNo: string) => {
    const out: Record<string, 0 | 1> = {}
    for (const inp of ctl.model?.inputs || []) {
      if (!inp.target || !locked.has(inp.name)) continue
      const t = parseTarget(inp.target, fi, form)
      if (t && t.formNo === formNo && t.scope !== 'var') out[keyOf(fi, formNo, t.name)] = 1
    }
    return out
  }
  const errorsFor = (list: FieldSpec[], formNo: string, rowIndex: number | null) => {
    const out: Record<string, string> = {}
    for (const f of list) {
      const key = keyOf(fi, formNo, f.target.name)
      const msg = ui.fieldErrors[f.spec]
        || issues.find(i => i.type === 'E' && i.field && keyOf(fi, formNo, i.field) === key
          && String(i.form_no || '') === formNo && (rowIndex == null || i.row == null || i.row === rowIndex))?.message
      if (msg) out[key] = msg
    }
    return out
  }
  const helpRows = fields.filter(f => f.help)
  const line = scope.line
  const lineSm = fi.forms[form]?.sm

  return (
    <TxnRuntimeProvider value={screen.txn}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {headerFields.length > 0 && fi.forms[fi.headerNo] && (
          <FormRenderer
            model={restrict(fi.forms[fi.headerNo].sm, fi, fi.headerNo, headerFields, group)}
            values={frame.record.header}
            errors={errorsFor(headerFields, fi.headerNo, null)}
            onChange={(key, v) => ctl.change(`header.${key}`, v)}
            readOnlyKey={!!frame.txn_id}
            project={host.project}
            objName={base.obj_name}
            dynamicProtect={{ ...(frame.protect || {}), ...lockedKeys(fi.headerNo) }}
            txnId={frame.txn_id || undefined}
            formNo={fi.headerNo}
            mode={frame.txn_id ? 'edit' : 'add'}
            layout={handheld ? 'compact' : 'default'}
          />
        )}
        {lineFields.length > 0 && lineSm && (line ? (
          <FormRenderer
            model={restrict(lineSm, fi, form, lineFields, headerFields.length ? (lineSm.form_title || 'Line') : group)}
            values={line}
            headerValues={frame.record.header}
            errors={errorsFor(lineFields, form, frame.line_index ?? null)}
            onChange={(key, v) => ctl.change(`line${form}.${key}`, v)}
            readOnlyKey={!!line._txn_id}
            project={host.project}
            objName={base.obj_name}
            dynamicProtect={{ ...((line._protect as Record<string, 0 | 1>) || {}), ...lockedKeys(form) }}
            formNo={form}
            mode={line._txn_id ? 'edit' : 'add'}
            layout={handheld ? 'compact' : 'default'}
          />
        ) : (
          <div style={muted}>There is no current line to fill in.</div>
        ))}
        {varFields.map(f => (
          <VarField key={f.spec} f={f} value={frame.vars[f.target.name]} handheld={handheld}
            error={ui.fieldErrors[f.spec]} onChange={v => ctl.change(f.spec, v)} />
        ))}
        {helpRows.length > 0 && (
          <ul style={{ ...muted, margin: 0, paddingLeft: 18 }}>
            {helpRows.map(f => (
              <li key={f.spec}><b>{f.label || (f.target.scope === 'var' ? f.target.name : columnLabel(fi, f.target))}</b>: {f.help}</li>
            ))}
          </ul>
        )}
      </div>
    </TxnRuntimeProvider>
  )
}

function columnLabel(fi: FormIndex, t: Target): string {
  const c = columnOf(fi, t.formNo, t.name)
  return String(c?.heading || c?.name || t.name)
}

function VarField({ f, value, onChange, handheld, error }: {
  f: FieldSpec; value: unknown; onChange: (v: unknown) => void; handheld: boolean; error?: string
}) {
  const label = f.label || f.target.name
  const type = f.type || 'string'
  const id = `wz-var-${f.target.name}`
  return (
    <div {...tw('field', `var.${f.target.name}`, { error: !!error })} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {type === 'boolean' ? (
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: handheld ? 48 : 32 }}>
          <input id={id} type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
            style={{ width: handheld ? 22 : 16, height: handheld ? 22 : 16 }} />
          <span>{label}</span>
        </label>
      ) : (
        <>
          <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{label}</label>
          <input id={id} style={inputStyle(handheld)}
            type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
            value={value == null ? '' : String(value)}
            onChange={e => onChange(type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value)} />
        </>
      )}
      {error && <div style={{ color: '#a61b1b', fontSize: 13 }}>{error}</div>}
    </div>
  )
}

